<template>
  <div class="flex flex-col gap-4" data-content-row>
    <div class="flex flex-row gap-2 items-center">
      <div class="w-6 h-6 rounded-full flex items-center justify-center" :style="{ backgroundColor: `${iconColor}15` }">
        <img :src="`/img/icons/${icon.toLowerCase()}.svg`" :alt="icon" class="w-4 h-4" />
      </div>
      <h1 class="text-xl font-medium text-text-primary flex gap-2 flex-1">
        {{ title }}
      </h1>
    </div>

    <div :class="[gridClasses, { 'items-start': isAnyHovered || hasSelectedMod, 'items-stretch': !isAnyHovered && !hasSelectedMod }]">
      <ModBlock
        v-for="(mod, index) in displayedMods"
        :key="mod.id"
        :mod="mod"
        :category="getModCategory(mod)"
        :showCategory="showCategory"
        @selectMod="selectMod"
        :selectedMod="selectedMod"
        @hover-change="handleHoverChange"
      />
    </div>
  </div>
</template>

<script setup>
import {
  ref, computed, onMounted, onUnmounted,
} from 'vue';
import { useStore } from 'vuex';
import ModBlock from './ModBlock.vue';

const emit = defineEmits(['selectMod']);
const isAnyHovered = ref(false);
let hoverTimeout = null;

// Reactive screen width detection
const screenWidth = ref(window.innerWidth);

const updateScreenWidth = () => {
  screenWidth.value = window.innerWidth;
};

onMounted(() => {
  window.addEventListener('resize', updateScreenWidth);
});

onUnmounted(() => {
  window.removeEventListener('resize', updateScreenWidth);
});

// Determine max mods to show based on screen size
const maxModsToShow = computed(() => (screenWidth.value > 2000 ? 5 : 4));

// Responsive grid classes
const gridClasses = computed(() => {
  if (screenWidth.value > 2000) {
    return 'grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6';
  }
  return 'grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-6';
});

// Limit mods based on screen size (only if limitMods is true)
const displayedMods = computed(() => {
  if (props.limitMods) {
    return props.mods.slice(0, maxModsToShow.value);
  }
  return props.mods;
});

// Get current grid columns based on screen size
const currentGridCols = computed(() => {
  if (screenWidth.value >= 1280) { // xl breakpoint
    return screenWidth.value > 2000 ? 5 : 4;
  } if (screenWidth.value >= 768) { // md breakpoint
    return 3;
  }
  return 1;
});

// Check if any mod in this row is selected (has a tooltip showing)
// If not, check if any child mod is selected
const hasSelectedMod = computed(() => displayedMods.value.some((mod) => mod.id === props.selectedMod?.id) || displayedMods.value.some((mod) => mod.children?.some((child) => child.id === props.selectedMod?.id)));

function selectMod(mod) {
  emit('selectMod', mod);
}

function handleHoverChange(isHovered) {
  if (isHovered) {
    // Immediately set to hovered state
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      hoverTimeout = null;
    }
    isAnyHovered.value = true;
  } else {
    // Delay the unhovered state to allow animation to complete
    hoverTimeout = setTimeout(() => {
      isAnyHovered.value = false;
      hoverTimeout = null;
    }, 300); // Match the animation duration
  }
}

function getModCategory(mod) {
  // Add null check for mod
  if (!mod || !mod.id) return null;

  // Find which category this mod belongs to
  const category = props.modCategories.find((cat) => cat.mods.some((catMod) => catMod.id === mod.id));
  return category ? {
    name: category.name,
    color: category.color,
  } : null;
}

const props = defineProps({
  mods: {
    type: Array,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  icon: {
    type: String,
    required: true,
  },
  iconColor: {
    type: String,
    required: true,
  },
  selectedMod: {
    type: Object,
    required: true,
  },
  showCategory: {
    type: Boolean,
    required: true,
  },
  modCategories: {
    type: Array,
    required: false,
    default: () => [],
  },
  limitMods: {
    type: Boolean,
    required: false,
    default: true,
  },
});
</script>
